import {
  ChangeDetectionStrategy,
  Component,
  input,
  output,
} from '@angular/core';
import { TranslocoPipe } from '@jsverse/transloco';
import { TabComponent } from '../tab/tab.component';
import { TabsComponent } from '../tabs/tabs.component';
import { TabsSize } from '../tabs/tabs.enum';
import { TagVariant } from '../tag/tag-variant.enum';

export interface SegmentedControlTag {
  readonly label: string;
  readonly type: TagVariant;
}

export interface SegmentedControlItem<T extends string = string> {
  readonly label: string;
  readonly value: T;
  readonly tag?: SegmentedControlTag;
}

@Component({
  selector: 'ct-lib-segmented-control',
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <ct-lib-tabs
      [embedded]="true"
      [autowidth]="autowidth()"
      [size]="size()"
      [checkDescendants]="false"
    >
      @for (item of items(); track item.value) {
        <ct-lib-tab
          [label]="item.label | transloco"
          [tag]="
            item.tag
              ? {
                  label: (item.tag.label | transloco),
                  type: item.tag.type,
                }
              : null
          "
          [active]="selectedValue() === item.value"
          (onOpen)="emitSelection(item.value)"
        >
          <span class="visually-hidden">&nbsp;</span>
        </ct-lib-tab>
      }
    </ct-lib-tabs>
  `,
  styleUrls: ['./segmented-control.component.scss'],
  imports: [TabsComponent, TabComponent, TranslocoPipe],
  host: {
    '[class.is-autowidth]': 'autowidth()',
  },
})
export class SegmentedControlComponent<T extends string = string> {
  readonly items = input.required<ReadonlyArray<SegmentedControlItem<T>>>();
  readonly selectedValue = input.required<T>();
  readonly size = input(TabsSize.SMALL);
  readonly autowidth = input(true);

  readonly valueChange = output<T>();

  /**
   * Emits the selected segment value.
   * @param {T} value Selected segment value
   * @returns {void}
   */
  emitSelection(value: T): void {
    this.valueChange.emit(value);
  }
}
